import { Paper, Toolbar, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { ApiClient } from 'ApiClient';
import {
  ConfirmDialog,
  ResponsiveIconButton,
  SnackbarContext,
  SortableTable,
  TableControls,
} from 'components';
import { SortableTableHeader } from 'components/SortableTable/components';
import { useApiClient } from 'hooks';
import { User } from 'models';
import * as React from 'react';
import { SaveUserDialog } from './components';

export const Users: React.FC = () => {
  const snackbar = React.useContext(SnackbarContext);

  const [{ data: users, refetch }] = useApiClient<User[]>('users');

  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<User>();

  const columns: SortableTableHeader[] = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'emailAddress', label: 'Email Address' },
    { key: 'controls', sortable: false },
  ];

  const rows =
    users?.map((user) => ({
      key: user.id.toString(),
      cells: [
        { key: 'firstName', display: user.name },
        { key: 'lastName', display: user.surname ?? '' },
        { key: 'emailAddress', display: user.emailAddress ?? '' },
        {
          key: 'controls',
          display: (
            <TableControls
              onEdit={(e) => {
                setModalOpen(true);
                setSelectedUser(user);
              }}
              onDelete={(e) => {
                setConfirmOpen(true);
                setSelectedUser(user);
              }}
            />
          ),
        },
      ],
    })) ?? [];

  return (
    <Paper>
      <Toolbar style={{ justifyContent: 'space-between' }}>
        <Typography variant="h6">Users</Typography>

        <ResponsiveIconButton
          onClick={(e) => {
            setSelectedUser(undefined);
            setModalOpen(true);
          }}
          color="primary"
          icon={AddIcon}
        >
          Add User
        </ResponsiveIconButton>
      </Toolbar>

      <SortableTable
        columns={columns}
        rows={rows}
        emptyTableText="No users available yet."
        emptyTableCreateAction={(e) => setModalOpen(true)}
        disablePagination
      />

      <SaveUserDialog
        user={selectedUser}
        refetch={refetch}
        dialogProps={{
          open: modalOpen,
          onClose: (e) => setModalOpen(false),
          fullWidth: true,
          maxWidth: 'sm',
        }}
      />

      <ConfirmDialog
        title="Confirm"
        content="Are you sure you would like to delete this user?"
        onConfirm={deleteUser}
        dialogProps={{
          open: confirmOpen,
          onClose: (e) => {
            setConfirmOpen(false);
            setSelectedUser(undefined);
          },
        }}
      />
    </Paper>
  );

  async function deleteUser() {
    try {
      if (!selectedUser) {
        return;
      }

      const response = await ApiClient.delete(`users/${selectedUser.id}`);

      if (!response) {
        return;
      }

      refetch();

      snackbar.open('User has been deleted successfully');
    } catch (error) {
      snackbar.error(error);
    }
  }
};
